function normalizeText(value) {
	if (value === undefined || value === null) {
		return "";
	}
	return String(value).trim();
}

const FRESH_INSTANCE_AUTHORIZATION_ISSUER = "ambifi-cloud";
const FRESH_INSTANCE_AUTHORIZATION_INTENT = "create";
const FRESH_INSTANCE_AUTHORIZATION_SOURCE = "cloudStart";
const FRESH_INSTANCE_AUTHORIZATION_PROTOCOL_VERSION = 1;
const LAUNCH_PROVENANCE_PROTOCOL_VERSION = 1;
const LAUNCH_PROVENANCE_INTENTS = new Set(["create", "resume"]);

function normalizeVersion(version = {}) {
	if (!version || typeof version !== "object" || Array.isArray(version)) {
		return "";
	}
	return [version.major, version.minor, version.revision, version.build]
		.map(normalizeText)
		.filter(Boolean)
		.join(".");
}

/**
 * Produce a compact, non-secret audit envelope that follows the launch into
 * Player and, ultimately, the saved activity's open-history event. This is
 * diagnostic evidence only; create authorization remains a separate exact,
 * one-shot contract.
 */
export function buildActivityInstanceLaunch({
	launchId,
	intent,
	origin,
	source = "",
	activityId,
	activityInstanceId,
	cloudVersion = {},
	client = "web",
	appApiVersion = "",
	nativeAppLaunchId = "",
	launchedAt = new Date().toISOString()
} = {}) {
	const normalizedIntent = normalizeText(intent).toLowerCase();
	const launch = {
		protocolVersion: LAUNCH_PROVENANCE_PROTOCOL_VERSION,
		launchId: normalizeText(launchId),
		intent: normalizedIntent,
		origin: normalizeText(origin),
		source: normalizeText(source),
		client: "ambifiCloud",
		platform: normalizeText(client) || "web",
		version: normalizeVersion(cloudVersion),
		appApiVersion: normalizeText(appApiVersion),
		nativeAppLaunchId: normalizeText(nativeAppLaunchId),
		activityId: normalizeText(activityId),
		instanceId: normalizeText(activityInstanceId),
		timestamp: normalizeText(launchedAt)
	};

	if (!launch.launchId ||
		!LAUNCH_PROVENANCE_INTENTS.has(launch.intent) ||
		!launch.origin ||
		!launch.activityId ||
		!launch.instanceId ||
		!launch.timestamp) {
		return null;
	}

	return launch;
}

export function isExactActivityInstanceLaunch(launch, activityId, instanceId) {
	return !!launch &&
		typeof launch === "object" &&
		!Array.isArray(launch) &&
		launch.protocolVersion === LAUNCH_PROVENANCE_PROTOCOL_VERSION &&
		!!normalizeText(launch.launchId) &&
		LAUNCH_PROVENANCE_INTENTS.has(normalizeText(launch.intent).toLowerCase()) &&
		!!normalizeText(launch.origin) &&
		normalizeText(launch.activityId) === normalizeText(activityId) &&
		normalizeText(launch.instanceId) === normalizeText(instanceId);
}

export function isExactFreshInstanceAuthorization(authorization, activityId, instanceId) {
	if (!authorization || typeof authorization !== "object" || Array.isArray(authorization)) {
		return false;
	}

	const requestedActivityId = normalizeText(activityId);
	const requestedInstanceId = normalizeText(instanceId);
	return authorization.validated === true &&
		normalizeText(authorization.issuer) === FRESH_INSTANCE_AUTHORIZATION_ISSUER &&
		normalizeText(authorization.intent).toLowerCase() === FRESH_INSTANCE_AUTHORIZATION_INTENT &&
		normalizeText(authorization.source) === FRESH_INSTANCE_AUTHORIZATION_SOURCE &&
		authorization.protocolVersion === FRESH_INSTANCE_AUTHORIZATION_PROTOCOL_VERSION &&
		!!normalizeText(authorization.nonce) &&
		!!requestedActivityId &&
		!!requestedInstanceId &&
		normalizeText(authorization.activityId) === requestedActivityId &&
		normalizeText(authorization.activityInstanceId) === requestedInstanceId;
}

/**
 * Build the transient handoff after Cloud has deliberately crossed its
 * startNewActivity boundary. The UUID may have been generated immediately or
 * preallocated by an existing Cloud workflow/assignment flow; an ordinary
 * resume must never call this producer.
 */
export function buildFreshInstanceAuthorization({
	activityId,
	activityInstanceId,
	nonce
} = {}) {
	const authorization = {
		validated: true,
		issuer: FRESH_INSTANCE_AUTHORIZATION_ISSUER,
		intent: FRESH_INSTANCE_AUTHORIZATION_INTENT,
		source: FRESH_INSTANCE_AUTHORIZATION_SOURCE,
		protocolVersion: FRESH_INSTANCE_AUTHORIZATION_PROTOCOL_VERSION,
		activityId: normalizeText(activityId),
		activityInstanceId: normalizeText(activityInstanceId),
		nonce: normalizeText(nonce)
	};

	return isExactFreshInstanceAuthorization(
		authorization,
		authorization.activityId,
		authorization.activityInstanceId
	) ? authorization : null;
}

/**
 * Remove the transient marker after the embedded Player has loaded. The
 * returned state preserves unrelated router state and can safely be passed to
 * browserHistory.replace.
 */
export function removeFreshInstanceAuthorizationFromLocationState(locationState) {
	if (!locationState || typeof locationState !== "object" || Array.isArray(locationState) ||
		!Object.prototype.hasOwnProperty.call(locationState, "freshInstanceAuthorization")) {
		return locationState;
	}

	const nextState = { ...locationState };
	delete nextState.freshInstanceAuthorization;
	return Object.keys(nextState).length > 0 ? nextState : undefined;
}

/**
 * Resuming arbitrary organization history opens the underlying activity in the
 * editable Player, so this permission must remain narrower than Status-board
 * permissions such as viewing jobs or changing workflow.
 */
export function canResumeAnyHistoryInCurrentOrg({
	isOrganizationAdministrator = false
} = {}) {
	return isOrganizationAdministrator === true;
}

function normalizeResumeSession(record, activityId, instanceId, overrideUrl = "") {
	if (!record || typeof record !== "object") {
		return null;
	}

	const requestedActivityId = normalizeText(activityId);
	const requestedInstanceId = normalizeText(instanceId);
	const recordActivityId = normalizeText(record.id || record.activityId);
	const recordInstanceId = normalizeText(record.instanceId || record.objectID);
	const url = normalizeText(overrideUrl || record.url || record.instanceUrl);

	if (!requestedActivityId || !requestedInstanceId ||
		recordActivityId !== requestedActivityId ||
		recordInstanceId !== requestedInstanceId ||
		!url) {
		return null;
	}

	return {
		...record,
		id: requestedActivityId,
		instanceId: requestedInstanceId,
		objectID: normalizeText(record.objectID) || requestedInstanceId,
		url
	};
}

export function findExactResumeSession(sessions, activityId, instanceId) {
	const candidates = Array.isArray(sessions) ? sessions : [];
	for (let index = 0; index < candidates.length; index++) {
		const session = normalizeResumeSession(candidates[index], activityId, instanceId);
		if (session) {
			return session;
		}
	}
	return null;
}

export function buildHistoryResumeFilters({
	instanceId,
	identityId,
	unrestricted = false
} = {}) {
	const requestedInstanceId = normalizeText(instanceId);
	const requestedIdentityId = normalizeText(identityId);

	if (!requestedInstanceId || (!unrestricted && !requestedIdentityId)) {
		return null;
	}

	const filters = [`objectID:${JSON.stringify(requestedInstanceId)}`];
	if (!unrestricted) {
		filters.push(`identityId:${JSON.stringify(requestedIdentityId)}`);
	}

	return filters.join(" AND ");
}

/**
 * Resolve an existing activity instance without ever treating a missing cached
 * session as permission to create a new object under the requested instance ID.
 */
export async function resolveActivityResume({
	activityId,
	instanceId,
	providedUrl = "",
	sessions = [],
	loadHistorySession,
	startIfHistoryRecordMissing = false
} = {}) {
	const requestedActivityId = normalizeText(activityId);
	const requestedInstanceId = normalizeText(instanceId);
	const requestedUrl = normalizeText(providedUrl);
	const cachedSession = findExactResumeSession(sessions, requestedActivityId, requestedInstanceId);

	// The cached session is already part of the caller's authorized session set
	// and contains the canonical object key. A stale URL supplied by a link or
	// caller must not override it.
	if (cachedSession) {
		return {
			status: "resolved",
			source: "sessionCache",
			url: cachedSession.url,
			session: cachedSession
		};
	}

	if (requestedUrl && requestedActivityId && requestedInstanceId) {
		return {
			status: "resolved",
			source: "providedUrl",
			url: requestedUrl,
			session: normalizeResumeSession(
				{
					id: requestedActivityId,
					instanceId: requestedInstanceId,
					objectID: requestedInstanceId
				},
				requestedActivityId,
				requestedInstanceId,
				requestedUrl
			)
		};
	}

	if (!requestedInstanceId) {
		return {
			status: "startNew",
			source: "missingInstanceId"
		};
	}

	if (typeof loadHistorySession !== "function") {
		return {
			status: "blocked",
			source: "historyLookupUnavailable"
		};
	}

	try {
		const historyRecord = await loadHistorySession(requestedInstanceId);
		if (!historyRecord) {
			if (startIfHistoryRecordMissing === true) {
				return {
					status: "startNew",
					source: "authorizedHistoryRecordMissing"
				};
			}
			return {
				status: "blocked",
				source: "historyRecordMissing"
			};
		}
		const historySession = normalizeResumeSession(
			historyRecord,
			requestedActivityId,
			requestedInstanceId
		);
		if (!historySession) {
			return {
				status: "blocked",
				source: "historyRecordInvalid"
			};
		}

		return {
			status: "resolved",
			source: "historyIndex",
			url: historySession.url,
			session: historySession
		};
	} catch (error) {
		return {
			status: "blocked",
			source: "historyLookupFailed",
			error
		};
	}
}

/**
 * A newly-created tracked activity can have an exact browser-local baseline
 * before its first history object is searchable. Cloud may hand that one
 * narrow miss to Player, which still requires an exact activity/instance
 * session or local draft and therefore remains fail-closed.
 */
export function shouldDelegateMissingExactResumeToPlayer({
	allowExactPlayerRouteRecovery = false,
	activityId = "",
	instanceId = "",
	resumeResolution = null
} = {}) {
	return allowExactPlayerRouteRecovery === true &&
		!!normalizeText(activityId) &&
		!!normalizeText(instanceId) &&
		!!resumeResolution &&
		resumeResolution.status === "blocked" &&
		resumeResolution.source === "historyRecordMissing";
}
